import type { AxiosResponse } from 'axios'

import type { InsightsFilterValuesEnvelope, InsightsService } from '@percent/domain/insights'

export const CANDIDATE_STATES_FILTER = 'candidateStates'
const PROGRAM_NAMES_FILTER = 'programNames'

export type CandidateStateLabels = {
  // display name -> every state id it covers, across the partner's programmes
  idsByLabel: Record<string, string[]>
  // state id -> display name, for the by-state chart
  labelById: Record<string, string>
  // programme name -> the display names its picker offers
  labelsByProgram: Record<string, string[]>
}

type Params = Record<string, string | string[] | number>

const toStateIds = (params: Params, { idsByLabel }: CandidateStateLabels): Params => {
  const selected = params[CANDIDATE_STATES_FILTER]

  if (selected === undefined) return params
  const labels = Array.isArray(selected) ? selected : [String(selected)]
  const ids = [...new Set(labels.flatMap(label => idsByLabel[label] ?? [label]))]

  return { ...params, [CANDIDATE_STATES_FILTER]: ids }
}

const asArray = (value: Params[string] | undefined): string[] => {
  if (value === undefined) return []

  return Array.isArray(value) ? value : [String(value)]
}

const localFilterValues = (params: Params, labels: string[]): AxiosResponse<InsightsFilterValuesEnvelope> => {
  const lookup = asArray(params.value)
  const search = typeof params.search === 'string' ? params.search.toLowerCase() : ''
  const matching = lookup.length
    ? labels.filter(label => lookup.includes(label))
    : labels.filter(label => label.toLowerCase().includes(search))

  return {
    data: {
      object: 'insights_filter_values',
      data: matching.map(label => ({ value: label, label })),
      pageSize: matching.length,
      _links: {}
    },
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {} as AxiosResponse['config']
  }
}

const byName = (a: string, b: string) => a.localeCompare(b)

// A lookup resolves labels already selected, so it reads every option; only the offer narrows.
const optionsFor = (params: Params, options: string[], { labelsByProgram }: CandidateStateLabels): string[] => {
  const programs = asArray(params[PROGRAM_NAMES_FILTER])

  if (programs.length === 0 || params.value !== undefined) return options

  return [...new Set(programs.flatMap(program => labelsByProgram[program] ?? []))].sort(byName)
}

/**
 * The candidates report filters and charts by the candidate's raw state id, because a state's
 * display name is defined per programme. This wraps the insights service so the state picker
 * offers one option per display name — only the selected programmes' names once any is
 * selected — and every request sends the ids that name covers. The URL, the picker and the
 * badges all carry the name; only the API sees the ids.
 */
export const withCandidateStateLabels = (service: InsightsService, labels: CandidateStateLabels): InsightsService => {
  const options = Object.keys(labels.idsByLabel).sort(byName)
  const { getFilterValues, exportDataset } = service

  return {
    ...service,
    getFilters: async args => {
      const response = await service.getFilters(args)

      return {
        ...response,
        data: {
          ...response.data,
          data: response.data.data.map(def =>
            def.key === CANDIDATE_STATES_FILTER
              ? {
                  ...def,
                  values: options.map(label => ({ value: label, label })),
                  hasMoreValues: false,
                  dependsOn: [PROGRAM_NAMES_FILTER]
                }
              : def
          )
        }
      }
    },
    getFilterValues: getFilterValues
      ? async args =>
          args.filter === CANDIDATE_STATES_FILTER
            ? localFilterValues(args.params, optionsFor(args.params, options, labels))
            : getFilterValues(args)
      : undefined,
    getMetric: args => service.getMetric({ ...args, params: toStateIds(args.params, labels) }),
    getDataset: args => service.getDataset({ ...args, params: toStateIds(args.params, labels) }),
    exportDataset: exportDataset
      ? args => exportDataset({ ...args, params: toStateIds(args.params, labels) })
      : undefined
  }
}
